import { faCheck, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BaseSyntheticEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import { selectEmailId, selectIsAdmin, selectIsLoggedIn } from '../../redux/account';
import { selectControls } from '../../redux/controls';
import Modal from '../Modals/Modal';
import NQTCheckBox from '../NQTCheckBox/NQTCheckBox';
import { NQTRadioButton } from '../NQTRadioButton/NQTRadioButton';
import { Spinner } from '../Spinner/Spinner';
import { PublishNotificationsDict, TODAY } from './models/PublishTestPanel.constants';
import { DeepLinkResponse, IValidationResultResponse, PublishCheckbox, PublishTestPanelProps, PublishTestRequest } from './models/PublishTestPanel.interfaces';
import { NOTIFY_TYPES, VALIDATION_CALL_TYPE } from './models/PublishTestPanel.types';
import './PublishTestPanel.scss';
import PublishLoginModal from '../Modals/PublishLoginModal/PublishLoginModal';
import { ICheckBox } from '../NQTCheckBox/NQTCheckBox.models';
import { selectIdsForDownload } from '../../redux/queryResults';
import { createDeepLinkAsync, getValidationResult, publishTestAsync, tabToSelectView } from './models/PublishTestPanel.utils';
import { selectMaxTestTakersPerCustomTest } from '../../redux/appSettings';
import { Roster } from '../Modals/models';
import { selectRosterList, selectRostersLoading } from '../../redux/rosters';
import ga4 from '../../shared/analyticsEvents';
import { ErrorResponse, isType } from '../../shared';
import { ReactComponent as DeleteIcon } from '../../images/nde-icon-delete.svg';


const staticMenuStyle = {
  width: '100%'
};

type PendingValidatedAction = 'NONE' | 'PUBLISH' | 'DEEP_LINK';

/**
 * Generates first Menu item element, of which should label dropdown items
 */
const genDDMenuItemLabel = (label?: string, callback?: () => void): JSX.Element => {
  return (
    <li>
      <button onClick={callback} className="dropdown-item" aria-label={label} tabIndex={0}>
        {label ? label : 'Label not found'}
      </button>
    </li>
  );
};

export const PublishTestPanel = ({ tabData }: PublishTestPanelProps): JSX.Element => {
  const panelRef = useRef<HTMLButtonElement>(null);
  const dateDropdown = useRef<HTMLButtonElement>(null);
  const totalTestTakersRef = useRef<HTMLInputElement>(null);
  const lastRadioInput = useRef<HTMLSpanElement | null>(null);
  const publishButton = useRef<HTMLButtonElement>(null);

  const controls = useSelector(selectControls);
  const idsForDownload = useSelector(selectIdsForDownload);
  const emailId = useSelector(selectEmailId);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isAdmin = useSelector(selectIsAdmin);
  const MaxTestTakersPerCustomTest = useSelector(selectMaxTestTakersPerCustomTest);
  const rosterList = useSelector(selectRosterList);
  const rosterLoading = useSelector(selectRostersLoading);

  //#region setStates

  const [focusOnSelectAll, setFocusOnSelectAll] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>('');

  const [isOkCancelModalOpen, setIsOkCancelModalOpen] = useState<boolean>(false);

  const [selectedRoster, setSelectedRoster] = useState<Roster | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [doPublish, setDoPublish] = useState<boolean>(false);

  const [publishExpanded, setPublishExpanded] = useState(false);
  const [deepLinkExpanded, setDeepLinkExpanded] = useState(false);

  const [allowResults, setAllowResults] = useState<ICheckBox>({ isSelected: true });
  const [allowMultiple, setAllowMultiple] = useState<ICheckBox>({ isSelected: false });

  const [modalHeaderText, setModalHeaderText] = useState<string>('');
  const [multiLineTextForHeader, setMultiLineTextForHeader] = useState<boolean>(false);


  const [date, setDate] = useState<Date>(new Date());
  const [dateLabel, setDateLabel] = useState<string>('');

  const [notifications, setNotifications] = useState<PublishCheckbox[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<NOTIFY_TYPES>(NOTIFY_TYPES.NONE);

  const [isValid, setIsValid] = useState<boolean>(false);
  const [isValidationLoading, setIsValidationLoading] = useState<boolean>(false);
  const [apiAbortController, setApiAbortController] = useState<AbortController>();
  const [pendingValidatedAction, setPendingValidatedAction] = useState<PendingValidatedAction>('NONE');

  const [isDeepLinkModalOpen, setIsDeepLinkModalOpen] = useState<boolean>(false);
  const [deepLinkResult, setDeepLinkResult] = useState<DeepLinkResponse | null>(null);
  const [isDeepLinkModalCompact, setIsDeepLinkModalCompact] = useState<boolean>(false);
  const [isCopyFeedbackVisible, setIsCopyFeedbackVisible] = useState<boolean>(false);
  const deepLinkInputRef = useRef<HTMLInputElement>(null);
  const copyFeedbackTimeoutRef = useRef<number | null>(null);
  //#endregion setStates

  /**
  * While creating a test, Do not allow user to choose a date that is greater than 1 year from the current date
  */
  const currentDate = new Date();
  const maxDateForTestCreation = new Date();
  maxDateForTestCreation.setFullYear(currentDate.getFullYear() + 1);






  //#region Function Callbacks

  const onDateChangeHandler = useCallback((date: Date) => {
    setDate(date);
    if (date.toLocaleDateString() === TODAY) {
      setDateLabel('');
    } else {
      setDateLabel(date.toLocaleDateString());
    }
    if (dateDropdown.current) {
      dateDropdown.current.click();
    }
  }, []);

  const onInputChangeHandler = useCallback((event: FormEvent<HTMLInputElement>) => {
    const num = +event.currentTarget.value;
    if (!isNaN(num) && num > 0) {
      if (num > 200) {
        event.currentTarget.value = '200';
      }
      setIsValid(true);
      setSelectedRoster(null);
    } else {
      event.currentTarget.value = '0';
      setIsValid(!!selectedRoster);
    }
  }, [selectedRoster]);

  useEffect(() => {
    return () => {
      apiAbortController?.abort();
    };
  }, [apiAbortController]);

  const onClickRosterDropDown = useCallback((event: BaseSyntheticEvent, roster?: Roster) => {
    event.preventDefault();
    if (!roster || !roster.rosterID) {
      setSelectedRoster(null);
      if (totalTestTakersRef.current && +totalTestTakersRef.current.value > 0) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
      return;
    }
    if (roster.rosterID === selectedRoster?.rosterID) {
      return;
    } else {
      setSelectedRoster(cloneDeep(roster));
      setIsValid(true);
      if (totalTestTakersRef.current) {
        totalTestTakersRef.current.value = '';
      }
    }
  }, [selectedRoster]);

  const resetState = useCallback(() => {
    if (totalTestTakersRef?.current) {
      totalTestTakersRef.current.value = '';
    }
    if (panelRef?.current) {
      !panelRef.current.classList.contains('collapsed') &&
        panelRef.current.click();
    }
    setModalHeaderText(controls?.PubOnlineTestHeader ? controls?.PubOnlineTestHeader?.controlText : '');
    setAllowResults({ isSelected: true });
    setAllowMultiple({ isSelected: false });
    setDate(new Date());
    setDateLabel('');
    setSelectedNotification(NOTIFY_TYPES.NONE);
    setSelectedRoster(null);
    setIsValid(false);
  }, [controls?.PubOnlineTestHeader]);

  const prepareTestRequest = useCallback((): PublishTestRequest => {
    const publishTestObj = {
      ItemTableIDList: (idsForDownload && tabData) ? tabData.filter(tb => !!idsForDownload[tb.itemTableID]).map(tb => tb.itemTableID) : [] as number[],
      NotifyOption: selectedNotification,
      NumTestTakers: totalTestTakersRef.current?.value ? totalTestTakersRef.current.value : undefined,
      DueDate: date.toLocaleDateString() === TODAY ? undefined : date,
      TestTakersSeeResults: allowResults.isSelected,
      MultipleAttempts: allowMultiple.isSelected,
      RosterID: selectedRoster ? selectedRoster.rosterID : undefined,
      SelRosterName: selectedRoster ? selectedRoster.rosterName : undefined
    } as PublishTestRequest;
    return publishTestObj;

  }, [
    allowMultiple.isSelected,
    allowResults.isSelected,
    date,
    idsForDownload,
    selectedNotification,
    selectedRoster,
    tabData
  ]);

  const doApiValidation = useCallback(async(validationCallType: VALIDATION_CALL_TYPE): Promise<IValidationResultResponse | ErrorResponse> => {
    const itemslist = ((idsForDownload !== null) && (Object.keys(idsForDownload).length)) ?
      Object.keys(idsForDownload).map(id => +id)
      : [];

    if (apiAbortController) apiAbortController.abort();
    const abortController = new AbortController();
    setApiAbortController(abortController);
    setIsValidationLoading(true);
    setIsLoading(true);
    const result = await getValidationResult(itemslist, validationCallType, abortController);
    setIsValidationLoading(false);
    setIsLoading(false);
    return result;
  }
  , [apiAbortController, idsForDownload]);


  const runPublish = useCallback(async(emailId?: string): Promise<void> => {
    if (!doPublish) return;
    if (isLoggedIn || emailId) {
      setIsLoading(true);
      const publishTestObj = prepareTestRequest();
      ga4.onGA4PublishPaperTest(!!publishTestObj?.RosterID);
      await publishTestAsync(
        setIsModalOpen,
        setModalContent,
        controls,
        publishTestObj
      );
      resetState();
      setIsLoading(false);
    } else {
      setIsPublishModalOpen(true);
    }
  }, [doPublish, controls, isLoggedIn, prepareTestRequest, resetState]);

  const getSelectedItemIds = useCallback((): number[] => {
    return idsForDownload ?
      Object.keys(idsForDownload).map(id => idsForDownload[id].itemTableIDAsInt) :
      [];
  }, [idsForDownload]);

  const openDeepLinkErrorModal = useCallback((errorMsg: string) => {
    setIsDeepLinkModalCompact(true);
    setDeepLinkResult({
      deepLinkResultURL: '',
      errorMsg
    });
    setIsDeepLinkModalOpen(true);
  }, []);

  const runCreateDeepLink = useCallback(async(): Promise<void> => {
    const selectedItemIds = getSelectedItemIds();

    if (!selectedItemIds.length) {
      openDeepLinkErrorModal(controls?.PubPickItems?.controlText || 'Please select at least one item first.');
      return;
    }

    if (!emailId) {
      openDeepLinkErrorModal('Unable to determine logged in username.');
      return;
    }

    const currentUrl = new URL(window.location.href);
    currentUrl.pathname = currentUrl.pathname.replace(/\/searchquestions\/?$/i, '');

    setIsLoading(true);
    try {
      const response = await createDeepLinkAsync({
        ItemTableIDs: selectedItemIds,
        LoggedInUsername: emailId,
        CurrentApplicationURL: currentUrl.toString()
      });

      setIsDeepLinkModalCompact(false);
      setDeepLinkResult(response);
      setIsDeepLinkModalOpen(true);
    } catch (err) {
      const errorMsg =
        typeof err === 'string'
          ? err
          : err instanceof Error && err.message
            ? err.message
            : 'Error creating deep link.';
      setIsDeepLinkModalCompact(false);
      setDeepLinkResult({
        deepLinkResultURL: '',
        errorMsg
      });
      setIsDeepLinkModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [controls, emailId, getSelectedItemIds, openDeepLinkErrorModal]);

  const runPublishValidation = useCallback(async(
    nextAction: Exclude<PendingValidatedAction, 'NONE'>,
    validationCallType: VALIDATION_CALL_TYPE = VALIDATION_CALL_TYPE.AT_PUBLISH
  ): Promise<boolean> => {
    const apiResponse = await doApiValidation(validationCallType);
    if (isType<IValidationResultResponse>(apiResponse, 'ValidationResultResponse')) {
      if (!apiResponse.validationResult.isValid) {
        setPendingValidatedAction('NONE');
        setModalContent('<p>' + apiResponse.validationResult.testWarnings.join('</p><p>') + '</p>');
        setModalHeaderText(controls?.WarningHeaderText ? controls?.WarningHeaderText?.controlText : '');
        setMultiLineTextForHeader(true);
        setIsModalOpen(true);
        return false;
      }

      // valid api result
      // only show ok/cancel modal if there are warnings
      if (apiResponse.validationResult.testWarnings.length > 0) {
        setPendingValidatedAction(nextAction);
        setModalContent('<p>' + apiResponse.validationResult.testWarnings.join('</p><p>') + '</p>');
        setIsOkCancelModalOpen(true);
        if (apiResponse.validationResult.testWarnings.includes('Congratulations!')) {
          setModalHeaderText(controls?.PubOnlineTestHeader ? controls?.PubOnlineTestHeader?.controlText : '');
          setMultiLineTextForHeader(false);
        } else {
          setModalHeaderText(controls?.WarningHeaderText ? controls?.WarningHeaderText?.controlText : '');
          setMultiLineTextForHeader(true);
        }
        return false;
      }
    }

    setPendingValidatedAction('NONE');
    return true;
  }, [controls, doApiValidation]);

  const onPublishClick = useCallback(async(): Promise<void> => {
    if (!idsForDownload) {
      setModalContent(controls?.PubPickItems ? controls.PubPickItems?.controlText : '');
      setIsModalOpen(true);
      setFocusOnSelectAll(true);
      return;
    }
    const shouldContinue = await runPublishValidation('PUBLISH');
    if (!shouldContinue) {
      return;
    }
    setDoPublish(true);
  }
  , [controls, idsForDownload, runPublishValidation]);

  const onCreateDeepLinkClick = useCallback(async(): Promise<void> => {
    const selectedItemIds = getSelectedItemIds();

    if (!selectedItemIds.length) {
      openDeepLinkErrorModal(controls?.PubPickItems?.controlText || 'Please select at least one item first.');
      return;
    }

    if (!emailId) {
      openDeepLinkErrorModal('Unable to determine logged in username.');
      return;
    }

    const shouldContinue = await runPublishValidation('DEEP_LINK', VALIDATION_CALL_TYPE.CHECK_ALL);
    if (!shouldContinue) {
      return;
    }

    await runCreateDeepLink();
  }, [controls, emailId, getSelectedItemIds, openDeepLinkErrorModal, runCreateDeepLink, runPublishValidation]);

  const onCloseDeepLinkModal = useCallback(() => {
    if (copyFeedbackTimeoutRef.current) {
      window.clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = null;
    }
    setIsCopyFeedbackVisible(false);
    setIsDeepLinkModalOpen(false);
    setIsDeepLinkModalCompact(false);
    setDeepLinkResult(null);
  }, []);

  const copyDeepLinkToClipboard = useCallback(async() => {
    const deepLinkUrl = deepLinkResult?.deepLinkResultURL;
    if (!deepLinkUrl) {
      return;
    }

    let copiedToClipboard = false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(deepLinkUrl);
        copiedToClipboard = true;
      } catch {
        // Fallback to selected input + execCommand for older or restricted browsers.
      }
    }

    if (!copiedToClipboard && deepLinkInputRef.current) {
      deepLinkInputRef.current.select();
      copiedToClipboard = document.execCommand('copy');
    }

    if (copiedToClipboard) {
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
      setIsCopyFeedbackVisible(true);
      copyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setIsCopyFeedbackVisible(false);
        copyFeedbackTimeoutRef.current = null;
      }, 2000);
    }
  }, [deepLinkResult]);

  const onPublishModalClose = useCallback((emailId?: string) => {
    if (emailId) {
      setDoPublish(true);
    } else {
      resetState();
    }
    setIsPublishModalOpen(false);
  }, [resetState]);

  const updateSelectedNotifications = useCallback((val: string) => {
    if (val && val in PublishNotificationsDict) {
      if (PublishNotificationsDict[val] !== selectedNotification) {
        setSelectedNotification(PublishNotificationsDict[val]);
      }
    }
  }, [selectedNotification]);

  const closeMessageModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const closeOkCancelModal = useCallback((result?: boolean) => {
    setIsOkCancelModalOpen(false);
    if (!result) {
      setPendingValidatedAction('NONE');
      return;
    }

    if (pendingValidatedAction === 'PUBLISH') {
      setDoPublish(true);
    } else if (pendingValidatedAction === 'DEEP_LINK') {
      void runCreateDeepLink();
    }
    setPendingValidatedAction('NONE');
  }, [pendingValidatedAction, runCreateDeepLink]);
  //#endregion Function Callbacks

  useEffect(() => {
    if (doPublish) {
      runPublish();
      setDoPublish(false);
    }
  }, [doPublish, runPublish]);

  useEffect(() => {
    if (controls) {

      const formats = [
        {
          label: controls?.PubEachTest?.controlText,
          value: NOTIFY_TYPES.EACH,
          isSelected: false
        },
        {
          label: controls?.PubAllTests?.controlText,
          value: NOTIFY_TYPES.ALL,
          isSelected: false
        },
        {
          label: 'do not notify me',
          value: NOTIFY_TYPES.NONE,
          isSelected: true
        }
      ];
      setNotifications(formats);
    }
  }, [controls]);

  // fires when focusOnSelectAll is toggled - redirects focus to ListView checkboxes if true
  useEffect(() => {
    if (focusOnSelectAll && !isModalOpen) {
      const selectAllElement = document.querySelector('#table-header--select-all + .nqt-checkbox') as HTMLElement;
      if (selectAllElement) {
        selectAllElement.focus();
      }
      setFocusOnSelectAll(false);
    }
  }, [focusOnSelectAll, isModalOpen]);

  // fires when publishExpanded is toggle
  // adds and removes eventListeners for tabbing
  useEffect(() => {
    if (!publishExpanded) {
      // Add
      panelRef.current?.addEventListener('keydown', tabToSelectView);

      // Remove
      publishButton.current?.removeEventListener('keydown', tabToSelectView);
      lastRadioInput.current?.removeEventListener('keydown', tabToSelectView);

    } else {
      // Place eventListener on last radio button or publish button (if enabled)
      panelRef.current?.removeEventListener('keydown', tabToSelectView);

      if (isValid) {
        publishButton.current?.addEventListener('keydown', tabToSelectView);

        lastRadioInput.current?.removeEventListener('keydown', tabToSelectView);
      } else {
        lastRadioInput.current?.addEventListener('keydown', tabToSelectView);

        publishButton.current?.removeEventListener('keydown', tabToSelectView);
      }
    }
    // store references for clean up, incase refs change
    const panel = panelRef.current;
    const publish = publishButton.current;
    const radio = lastRadioInput.current;
    return () => {
      panel?.removeEventListener('keydown', tabToSelectView);
      publish?.removeEventListener('keydown', tabToSelectView);
      radio?.removeEventListener('keydown', tabToSelectView);
    };
  }, [publishExpanded, isValid]);

  useEffect(() => {
    if (publishExpanded) {
      if (!idsForDownload || Object.keys(idsForDownload).length === 0) {
        return;
      }
      doApiValidation(VALIDATION_CALL_TYPE.AT_DIALOG_OPEN).then((result) => {
        if (isType<IValidationResultResponse>(result, 'ValidationResultResponse')) {
          if (result.validationResult.testWarnings.length > 0) {
            setModalContent('<p>' + result.validationResult.testWarnings.join('</p><p>') + '</p>');
            setModalHeaderText(controls?.WarningHeaderText ? controls?.WarningHeaderText?.controlText : '');
            setIsModalOpen(true);
            setMultiLineTextForHeader(true);
            return;
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishExpanded]);


  useEffect(() => {
    return () => {
      apiAbortController?.abort();
    };
  });

  useEffect(() => {
    ReactModal.setAppElement('#root');
  }, []);

  useEffect(() => {
    if (!isDeepLinkModalOpen || !deepLinkResult || deepLinkResult.errorMsg || !deepLinkInputRef.current) {
      return;
    }

    deepLinkInputRef.current.focus();
    deepLinkInputRef.current.select();
  }, [deepLinkResult, isDeepLinkModalOpen]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className="nqt-ptp">
      <PublishLoginModal
        open={isPublishModalOpen}
        onClose={onPublishModalClose}
      />
      <Modal
        headerText={modalHeaderText != '' ? modalHeaderText : controls?.PubOnlineTestHeader?.controlText }
        bodyText={modalContent}
        windowType='DESC'
        open={isModalOpen}
        multiLineHeaderText={multiLineTextForHeader}
        shouldReturnFocusAfterClose={false}
        onClose={() => closeMessageModal()}
      />
      <Modal
        headerText={modalHeaderText != '' ? modalHeaderText : controls?.PubOnlineTestHeader?.controlText}
        bodyText={modalContent}
        windowType='PROMPT'
        open={isOkCancelModalOpen}
        multiLineHeaderText={multiLineTextForHeader}
        shouldReturnFocusAfterClose={false}
        onClose={(result) => closeOkCancelModal(result)}
      />
      {
        isDeepLinkModalOpen &&
        <ReactModal
          isOpen={isDeepLinkModalOpen}
          className={`nqt-modal nqt-ptp-deep-link-modal${isDeepLinkModalCompact ? ' nqt-ptp-deep-link-modal--compact' : ''}`}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          shouldReturnFocusAfterClose={true}
          contentLabel={controls?.DeepLnk?.controlText || 'Deep Link'}
        >
          <div className='nqt-modal--container'>
            <div className='nqt-modal--header-group'>
              <header className='nqt-modal--title'>{controls?.DeepLnk?.controlText || 'Deep Link'}</header>
              <button className='nqt-modal--close-button'
                ref={(el) => el?.focus()}
                onClick={onCloseDeepLinkModal}>
                <DeleteIcon />
                <span className="visually-hidden">{controls?.ModalClose?.controlText || 'Close'}</span>
              </button>
            </div>
            <div className='nqt-modal--border'>
              <section className='nqt-modal--content'>
                {
                  deepLinkResult?.errorMsg ?
                    <div className='nqt-ptp-deep-link-modal--error'>{deepLinkResult.errorMsg}</div>
                    :
                    <>
                      <label className='nqt-ptp-deep-link-modal--label' htmlFor='nqt-ptp-deep-link-input'>
                        {controls?.DeepLnkCreateMsg?.controlText}
                      </label>
                      <div className='nqt-ptp-deep-link-modal--controls'>
                        <input
                          id='nqt-ptp-deep-link-input'
                          ref={deepLinkInputRef}
                          className='nqt-ptp-deep-link-modal--input'
                          readOnly
                          value={deepLinkResult?.deepLinkResultURL || ''}
                          type='text'
                        />
                        <div className='nqt-ptp-deep-link-modal--copy-btn-wrap'>
                          {
                            isCopyFeedbackVisible &&
                            <span className='nqt-ptp-deep-link-modal--copy-success' role='status' aria-live='polite'>
                              <FontAwesomeIcon icon={faCheck} />
                            </span>
                          }
                          <button
                            className='nqt-ptp-deep-link-modal--copy-btn'
                            type='button'
                            onClick={() => copyDeepLinkToClipboard()}>
                            <span className='nqt-ptp-deep-link-modal--copy-btn-label'>Copy</span>
                          </button>
                        </div>
                      </div>
                    </>
                }
              </section>
              <hr />
              <div className='nqt-ptp-deep-link-modal--buttons'>
                <button className='nqt-button--submit'
                  type='button'
                  onClick={onCloseDeepLinkModal}>
                  {controls?.ModalOK?.controlText || 'OK'}
                </button>
              </div>
            </div>
          </div>
        </ReactModal>
      }
      <div className="row">
        <div className="col nqt-ptp--what-next mx-n2">
          <h3 className="nqt-ptp--what-next-header">
            {controls?.DoNext?.controlText}
          </h3>
          <p className="nqt-ptp--what-next-desc">
            {controls?.DoNextSub?.controlText}
          </p>
        </div>
      </div>
      <div className="accordion accordion-flush row gy-4" id="publish-online-test-accordion">

        {
          isLoading && <Spinner />
        }
        <div className="accordion-it" >
          {false && <Spinner />}
          <h3 className="accordion-header" id="ptpHeadingOne">
            <button
              id='nqt-ptp--accordion-button'
              className="nqt-ptp--accordion-button accordion-button"
              ref={panelRef}
              onClick={
                (e) => !e.currentTarget.classList.contains('collapsed') ?
                  setPublishExpanded(true) :
                  setPublishExpanded(false)
              }
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#ptpCollapseOne"
              aria-expanded={publishExpanded}
              aria-controls="ptpCollapseOne">
              <span>
                {
                  controls?.PubOnlineTestHeader?.controlText
                }
              </span>
              {
                publishExpanded ?
                  <FontAwesomeIcon className='fa-minus' icon={faMinus}></FontAwesomeIcon>
                  :
                  <FontAwesomeIcon className='fa-plus' icon={faPlus}></FontAwesomeIcon>
              }
            </button>
          </h3>

          <div id="ptpCollapseOne" className="accordion-collapse collapse" aria-labelledby="ptpHeadingOne" data-bs-parent="#publish-online-test-accordion">
            <div className="accordion-body px-1 py-2">
              <div className="row row-cols-1 gy-4">
                <div className="col">
                  <div className="row row-cols-1 gy-2">
                    <div className="col nqt-ptp--col-1">
                      <div className="nqt-ptp--enter-takers">
                        <label htmlFor="nqt-ptp--total-test-takers-control" className="nqt-ptp--enter-takers-label acc-header-g">{controls?.PubTestTakers?.controlText}</label>
                        <input className="nqt-ptp--enter-takers-input nqt-input-text"
                          id='nqt-ptp--total-test-takers-control'
                          ref={totalTestTakersRef}
                          onChange={onInputChangeHandler}
                          type="number"
                          min={0}
                          max={MaxTestTakersPerCustomTest}
                          maxLength={3} />
                      </div>
                      <div className="nqt-ptp--or acc-header-g">{controls?.PubTestOr?.controlText}</div>
                      <div className="dropdown nqt-ptp--dropdown">
                        {
                          rosterLoading && <Spinner />
                        }
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          disabled={!isLoggedIn || rosterList.length === 0}
                          type="button"
                          id="ptpDropdownMenuButton1"
                          data-bs-toggle="dropdown"
                          aria-expanded="false">
                          <span className='dropdown-interior'>
                            {
                              selectedRoster ?
                                selectedRoster.rosterName :
                                controls?.PubTestAssign?.controlText
                            }
                          </span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="ptpDropdownMenuButton1">
                          {genDDMenuItemLabel(controls?.PubTestAssign?.controlText, () => { setSelectedRoster(null); setIsValid(false); })}
                          {
                            rosterList.length > 0 &&
                            rosterList.map((rts, ind) => (
                              <li key={`prp-rts-${ind}`}><a className={'dropdown-item' + (rts.rosterID === selectedRoster?.rosterID ? ' selected' : '')} onClick={(e) => onClickRosterDropDown(e, rts)} href="#">{rts.rosterName}</a></li>
                            ))
                          }
                        </ul>
                      </div>
                      <hr className="nqt-prp--hr" />
                      <div className="dropdown nqt-ptp--dropdown">
                        <button className="nqt-dropdown-btn btn dropdown-toggle"
                          ref={dateDropdown}
                          type="button"
                          id="ptpDropdownMenuButton2"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          aria-expanded="false">
                          <span className='dropdown-interior'>
                            {
                              dateLabel ?
                                dateLabel :
                                controls?.PubDueDate?.controlText
                            }
                          </span>
                          <span className='dropdown-arrow'></span>
                        </button>
                        <ul className="dropdown-menu calendar" style={staticMenuStyle} aria-labelledby="ptpDropdownMenuButton2">
                          <Calendar
                            onChange={onDateChangeHandler}
                            minDetail='month'
                            minDate={new Date()}
                            maxDate={maxDateForTestCreation}
                            value={date}
                          />
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col nqt-p2p--col-2 my-3">
                <NQTCheckBox containerClass='tab-data'
                  label={controls?.PubResults?.controlText}
                  checkBoxObj={allowResults}
                  id='nqt-ptp--allow-results' />
                {/* <NQTCheckBox containerClass='tab-data'
                  label={controls?.PubRetakes?.controlText}
                  checkBoxObj={allowMultiple}
                  disabled={true}
                  id='nqt-ptp--allow-multiple'/> */}
              </div>
              <div className="nqt-ptp--questions col">
                <div className="row row-cols-1 gy-2">
                  <div className="nqt-ptp--questions-total col">
                    <h3 className="nqt-ptp--questions-header">
                      {controls?.PubNotifications?.controlText} &nbsp;
                    </h3>
                  </div>
                  <fieldset className="nqt-ptp--radio-buttons row row-cols-1" role="radiogroup">
                    <span className="nqt-ptp--label col">{controls?.PubNotify?.controlText}</span>
                    {
                      notifications.length > 0 &&
                      notifications.map((format, ind) => (
                        <div key={ind} className={`col${ind === notifications.length - 1 ? '-12' : ''} nqt-dp--radio`}>
                          <NQTRadioButton
                            ref={ind === notifications.length - 1 ? lastRadioInput : undefined}
                            checked={selectedNotification === format?.value}
                            id={'nqt-ptp--radio-' + ind}
                            name='nqt-publish-test-notifications'
                            radioClassContainer='nqt-ptp--radio-container'
                            value={format.value.toString()}
                            label={format.label}
                            updateHandler={updateSelectedNotifications}
                          />
                        </div>
                      ))
                    }
                  </fieldset>
                  <div className="nqt-ptp--questions-total col">
                    <button className='nqt-submit-button nqt-ptp--questions-button'
                      id='publish-button'
                      ref={publishButton}
                      disabled={!isValid || isLoading || isValidationLoading}
                      onClick={() => onPublishClick()}
                      type='submit'>
                      {controls?.PubPublish?.controlText?.toUpperCase()}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {
          isLoggedIn && isAdmin &&
          <div className="accordion-it" style={{ marginTop: isAdmin ? '16px' : '0' }}>
            <h3 className="accordion-header" id="ptpHeadingTwo">
              <button
                id='nqt-ptp--deep-link-accordion-button'
                className="nqt-ptp--accordion-button accordion-button collapsed"
                onClick={
                  (e) => !e.currentTarget.classList.contains('collapsed') ?
                    setDeepLinkExpanded(true) :
                    setDeepLinkExpanded(false)
                }
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#ptpCollapseTwo"
                aria-expanded={deepLinkExpanded}
                aria-controls="ptpCollapseTwo">
                <span>
                  {controls?.DeepLnk?.controlText}
                </span>
                {
                  deepLinkExpanded ?
                    <FontAwesomeIcon className='fa-minus' icon={faMinus}></FontAwesomeIcon>
                    :
                    <FontAwesomeIcon className='fa-plus' icon={faPlus}></FontAwesomeIcon>
                }
              </button>
            </h3>

            <div id="ptpCollapseTwo" className="accordion-collapse collapse" aria-labelledby="ptpHeadingTwo" data-bs-parent="#publish-online-test-accordion">
              <div className="accordion-body px-1 py-2">
                <div className="row row-cols-1 gy-2">
                  <div className="nqt-ptp--questions-total col">
                    <button className='nqt-submit-button nqt-ptp--questions-button'
                      type='button'
                      onClick={() => onCreateDeepLinkClick()}
                      disabled={isLoading}>
                      {controls?.DeepLnkCreate?.controlText?.toUpperCase()}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </section>
  );
};